/* eslint-disable max-len */
import React, { useContext, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useSWR from 'swr';
import {
  Box,
  Stack,
  Card,
  Typography,
  Button,
  Tooltip,
  Dialog,
  Chip,
  Divider,
  Alert,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import { AppContext, AuthContext } from 'Context';
import { getTrialAllowanceText } from 'Commons';
import { Pathname } from 'Routes';
import { axios, baseUrl, dataFetcher, endpoints } from 'Api';
import { logAppEvent } from 'Api/errorLogger';
import { V2SettingsShell } from '../../_shared/V2SettingsShell';
import { V2ThemeProvider } from '../../_shared/V2Layout';
import { PaymentMethodsManager } from './PaymentMethodsManager';

// Placeholder transaction history — the backend endpoint doesn't exist yet, so
// the table shows representative mock rows behind a "coming soon" banner.
const MOCK_TRANSACTIONS = [
  {
    id: 't1', amount: '$120.00', last4: '4242', date: 'Jul 12, 2026', billingName: 'Laynee Swenson', projector: "Laynee's Projector",
  },
  {
    id: 't2', amount: '$120.00', last4: '4242', date: 'Jun 12, 2026', billingName: 'Laynee Swenson', projector: "Laynee's Projector",
  },
  {
    id: 't3', amount: '$120.00', last4: '1881', date: 'May 12, 2026', billingName: 'Laynee Swenson', projector: 'Family Projector',
  },
  {
    id: 't4', amount: '$9.99', last4: '4242', date: 'Apr 12, 2026', billingName: 'Laynee Swenson', projector: "Laynee's Projector",
  },
];

const FieldRow = ({ icon: Icon, label, value, action }) => (
  <Stack
    direction={{ xs: 'column', sm: 'row' }}
    spacing={{ xs: 1, sm: 2 }}
    sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', py: 1.1 }}
  >
    <Stack direction="row" spacing={2} sx={{ alignItems: 'center', minWidth: 0, flex: 1 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: 'action.hover',
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 600, wordBreak: 'break-word', mt: 0.25 }}>
          {value || '—'}
        </Typography>
      </Box>
    </Stack>
    {action}
  </Stack>
);

export const BillingSettingsV2 = () => {
  const {
    userDetails, subscriptionDetails, setAppSnackbar, getSubscription,
    langText, autoSignIn, userAccessedData,
  } = useContext(AppContext);
  const { getToken } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const billPayment = langText?.settings?.billPayment;
  const commonText = langText?.common;
  const cancellationText = billPayment?.cancellation;

  const [isCancelClicked, setIsCancelClicked] = useState(false);
  // In-place payment-methods manager: instead of routing to a separate page,
  // "Manage" expands a full-width manager over the plan + billing-info cards.
  const [managing, setManaging] = useState(false);
  const [managingAutoAdd, setManagingAutoAdd] = useState(false);
  const [renewModalOpen, setRenewModalOpen] = useState(false);
  const [reactivating, setReactivating] = useState(false);
  const [openCencellationModal, setOpenCencellationModal] = useState(location.search?.includes(Pathname.cancelSubscription));
  const [showHistory, setShowHistory] = useState(false);
  const [cardDetails, setCardDetails] = useState({ brand: undefined, last4: undefined, lastAttempt: undefined, month: undefined, year: undefined });
  const [done, setDone] = useState(true);
  const [loader, setloader] = useState(false);
  const [trial_end, setTrial_end] = useState({ year: undefined, month: undefined, day: undefined });
  const [today, setToday] = useState({ year: undefined, month: undefined, day: undefined });
  const [isDisabled, setIsDisabled] = useState({ isDisabled: true, error: 'You are not allowed to cancel subscription at this time' });

  const isPaidPlan = (!!subscriptionDetails?.subscription && subscriptionDetails?.subscription?.id !== '6') || subscriptionDetails?.expired;
  const planName = subscriptionDetails?.subscription?.product_name;
  const planId = Array.isArray(subscriptionDetails?.plans) && subscriptionDetails.plans.length > 0
    ? subscriptionDetails.plans[subscriptionDetails.plans.length - 1]?.plan_id
    : subscriptionDetails?.plans;
  const subscription_id = subscriptionDetails?.subscription?.id;
  const purchaseId = subscriptionDetails?.subscription?.purchase_plan_id;
  const freePlan = parseInt(subscriptionDetails?.subscription?.plan_type, 10) === 3;
  const paymentProcessing = parseInt(subscriptionDetails?.subscription?.subscription_status, 10) === 4 && subscriptionDetails?.has_activePlan;
  const paymentRetry = parseInt(subscriptionDetails?.subscription?.subscription_status, 10) === 2 && subscriptionDetails?.has_activePlan;

  // Open the cancel popover in place (no route change / full page).
  const handleCancellation = () => setOpenCencellationModal(true);

  // Refresh subscription + profile on open so the page always reflects the
  // latest plan (e.g. after a manager upgrades this account, or right after a
  // plan change) instead of showing stale cached data. Mirrors classic billing.
  useEffect(() => {
    getSubscription();
    autoSignIn();
  }, []);

  useEffect(() => {
    const paymentMethods = subscriptionDetails?.paymentMethods;
    const defaultPaymentMethod = subscriptionDetails?.default_payment_methods;
    if (paymentMethods && defaultPaymentMethod) {
      const paymentMethod = paymentMethods.find(({ id }) => id === defaultPaymentMethod);
      const brand = paymentMethod?.card?.brand;
      const last4 = paymentMethod?.card?.last4;
      const month = paymentMethod?.card?.exp_month;
      const year = paymentMethod?.card?.exp_year;
      const lastAttempt = paymentMethod?.last_attempt;
      setCardDetails({ brand, last4, lastAttempt, month, year });
    }
  }, [subscriptionDetails]);

  useEffect(() => {
    if (location.search?.includes(Pathname.cancelSubscription)) setOpenCencellationModal(true);
    else setOpenCencellationModal(false);
  }, [location]);

  const endDateOfSub = subscriptionDetails?.end_date ? new Date(subscriptionDetails.end_date) : null;
  let formattedDate = null;
  if (endDateOfSub && !Number.isNaN(endDateOfSub)) {
    endDateOfSub.setDate(endDateOfSub.getDate() - 1);
    const day = String(endDateOfSub.getDate()).padStart(2, '0');
    const month = endDateOfSub.toLocaleString('en-US', { month: 'long' });
    const year = endDateOfSub.getFullYear();
    formattedDate = `${month} ${day}, ${year}`;
  }

  const { isValidating: cancellingSubscription, mutate: cancelSubscription } = useSWR(
    [endpoints.cancelSubscription, { plan_id: purchaseId }],
    {
      fetcher: (url, payload) => dataFetcher(url, payload),
      onSuccess: ({ success }) => {
        if (success) {
          getSubscription();
          setDone(false);
          setAppSnackbar({ isVisible: true, type: 'success', message: 'Membership removed' });
          navigate(Pathname.dashboard);
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong while canceling' });
        }
      },
      onError: () => setAppSnackbar({ isVisible: true, type: 'error', message: 'Oops! Something went wrong' }),
    },
  );

  const { isValidating: paymentRetrying, mutate: retryPayment } = useSWR(
    [endpoints.retryPayment, purchaseId],
    {
      fetcher: (url, sid) => dataFetcher(url, { subscription_id: sid }),
      onSuccess: ({ success, message }) => {
        if (success) {
          setAppSnackbar({ isVisible: true, type: 'success', message });
          setTimeout(() => window.location.replace(Pathname.dashboard), 3000);
        } else {
          setAppSnackbar({ isVisible: true, type: 'error', message });
        }
      },
      onError: (error) => {
        const responseData = error?.response?.data?.message;
        setAppSnackbar({ isVisible: true, type: 'error', message: responseData || 'Oops! Something went wrong.' });
      },
    },
  );

  useEffect(() => {
    if (subscriptionDetails?.subscription?.end_date) {
      const date = subscriptionDetails?.subscription?.end_date?.split(' ')[0].split('-');
      const d = new Date();
      const year = parseInt(date[0], 10);
      const month = parseInt(date[1], 10);
      const day = parseInt(date[2], 10);
      const todaysDate = { year: d.getFullYear(), month: d.getMonth(), day: d.getDate(), time: d.getTime() };
      setTrial_end({ year, month, day });
      setToday(todaysDate);
      if (todaysDate.year > year) setIsDisabled({ ...isDisabled, isDisabled: false });
      else if (todaysDate.month > month) setIsDisabled({ ...isDisabled, isDisabled: false });
      else if (todaysDate.month === month && todaysDate.day > day) setIsDisabled({ ...isDisabled, isDisabled: false });
      else setIsDisabled({ ...isDisabled, isDisabled: true });
    }
  }, [subscriptionDetails]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('autorenew') === 'yes') setRenewModalOpen(true);
  }, [today, trial_end]);

  const handleCancelSubscription = () => {
    setIsCancelClicked(true);
    cancelSubscription();
  };

  const endDateUTC = new Date(`${subscriptionDetails?.subscription?.end_date}Z`);
  const currentDate = new Date();
  const differenceInMs = endDateUTC - currentDate;
  const differenceInDays = Math.ceil(differenceInMs / (1000 * 60 * 60 * 24));
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1;
  const isExpired = cardDetails?.year < currentYear || (cardDetails?.year === currentYear && cardDetails?.month < currentMonth);
  const isMoreThan365Days = (endDateOfSub - currentDate) > 3650 * 24 * 60 * 60 * 1000;

  const subscribePlan = async () => {
    setloader(true);
    try {
      const myToken = await getToken();
      const apiUrl = `${baseUrl}/${endpoints.addPlanV2}`;
      const response = await axios.post(apiUrl, {
        token: myToken, plan_id: planId, is_free: 0, upgrade: true, trial: false,
      });
      setAppSnackbar({ isVisible: true, type: 'success', message: response?.data?.message || 'Plan purchased successfully' });
      getSubscription();
      autoSignIn();
    } catch (error) {
      getSubscription();
      autoSignIn();
      const responseData = error?.response?.data?.message;
      logAppEvent({
        userName: `${userDetails?.firstname || ''} ${userDetails?.lastname || ''}`.trim() || 'Guest',
        functionAffected: 'Purchase Plan',
        description: 'Plan purchase failed from billing settings',
        severity: 'high',
        errorMessage: responseData || error?.message || 'Plan purchase failed',
        responseStatus: error?.response?.status || null,
        responseBody: responseData || null,
        requestUrl: `${baseUrl}/${endpoints.addPlanV2}`,
        requestMethod: 'POST',
        component: 'BillingSettingsV2',
        userAction: 'Purchase plan called',
      });
      setAppSnackbar({ isVisible: true, type: 'error', message: responseData || 'Oops! Something went wrong while purchasing plan' });
    } finally {
      setloader(false);
    }
  };

  const reactivateSubscription = async () => {
    setReactivating(true);
    try {
      const myToken = await getToken();
      const apiUrl = `${baseUrl}/${endpoints.reactivateSubscription}`;
      const response = await axios.post(apiUrl, { token: myToken, plan_id: purchaseId });
      setAppSnackbar({ isVisible: true, type: 'success', message: response?.data?.message || 'Subscription reactivated successfully' });
      getSubscription();
      autoSignIn();
      setRenewModalOpen(false);
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      const responseData = error?.response?.data?.message;
      setAppSnackbar({ isVisible: true, type: 'error', message: responseData || 'Oops! Something went wrong while reactivating subscription' });
    } finally {
      setReactivating(false);
    }
  };

  const showCancelButton = subscription_id
    && subscriptionDetails?.has_activePlan
    && !freePlan
    && !paymentProcessing
    && subscriptionDetails?.subscription?.is_public !== '0'
    && subscriptionDetails?.subscription?.is_public !== 0
    && subscriptionDetails?.current_plan !== 'Lifetime';

  const cancelDisabled = paymentRetrying || subscriptionDetails?.subscription?.is_cancelled === '1';
  const cancelLabel = paymentRetry
    ? (billPayment?.reActivate || 'Reactivate')
    : subscriptionDetails?.subscription?.is_cancelled === '1'
      ? 'Subscription Canceled'
      : subscriptionDetails?.subscription?.is_trial === '1'
        ? 'Cancel Trial'
        : 'Cancel Subscription';

  // ── Single consolidated status banner ──────────────────────────────────
  // Replaces the scattered plan/card alerts + chips. Only shows for a problem
  // state, carries ONE reason + ONE primary action. Priority: failed payment
  // (retryable) > expired > processing > auto-renewal off.
  const cardOnFile = (subscriptionDetails?.paymentMethods?.length || 0) > 0 || !!cardDetails?.last4;
  const paymentFailed = paymentRetry || cardDetails?.lastAttempt?.payment_status === '0';
  const autoRenewOff = subscriptionDetails?.subscription?.is_cancelled === '1'
    && subscriptionDetails?.subscription?.is_public !== '0'
    && subscriptionDetails?.subscription?.is_public !== 0
    && subscriptionDetails?.current_plan !== 'Lifetime';
  const goUpgrade = () => navigate(userAccessedData ? '/affiliate' : Pathname.plans);

  let statusBanner = null;
  if (paymentFailed) {
    statusBanner = {
      severity: 'error',
      message: cardDetails?.lastAttempt?.failure_reason
        ? `Payment failed — ${cardDetails.lastAttempt.failure_reason}. Retry to resume your plan.`
        : 'We couldn’t process your last payment. Retry to resume your plan.',
      actionLabel: paymentRetrying ? 'Retrying…' : 'Retry purchase',
      onAction: () => retryPayment(),
      disabled: paymentRetrying,
      secondaryLabel: 'Change Plan',
      // Always land on the (redesigned) Plans page to pick a different plan.
      onSecondary: () => navigate(Pathname.plans),
    };
  } else if (subscriptionDetails?.expired) {
    statusBanner = {
      severity: 'error',
      message: 'Your plan has expired.',
      actionLabel: cardOnFile ? 'Upgrade' : 'Add a card',
      // A card is on file → straight to Plans to re-purchase; otherwise open the
      // in-place manager with the add-card dialog already open.
      onAction: () => {
        if (cardOnFile) { goUpgrade(); return; }
        setManagingAutoAdd(true);
        setManaging(true);
      },
    };
  } else if (paymentProcessing) {
    statusBanner = {
      severity: 'info',
      message: 'Your payment is processing. This can take a few minutes.',
    };
  } else if (autoRenewOff) {
    statusBanner = {
      severity: 'warning',
      message: `Auto-renewal is off${formattedDate ? `. Your plan expires on ${formattedDate}` : ''}.`,
      actionLabel: 'Reactivate',
      onAction: () => setRenewModalOpen(true),
    };
  }

  return (
    <>
      <V2SettingsShell
        // Only the Manage-Payment-Info sub-flow wants the page pinned to the
        // viewport (it lays its own column out against that height). The normal
        // billing view lays out in normal flow instead, so the cards are never
        // clipped into a scroll region and the section shortcuts below them sit
        // on screen as-is.
        fitViewport={managing}
        hideQuickLinks={managing}
        title={billPayment?.title || 'Billing & payments'}
        description={billPayment?.description || 'Manage your subscription, payment methods, and account billing details.'}
      >
        <Stack spacing={1.75} sx={managing ? { flex: 1, minHeight: 0 } : undefined}>
          {/* Single consolidated status banner — the only alert on the page. */}
          {statusBanner && (
            <Alert
              severity={statusBanner.severity}
              variant="outlined"
              sx={{
                borderRadius: 2,
                alignItems: 'center',
                '& .MuiAlert-message': { py: 0.5, fontWeight: 500 },
                '& .MuiAlert-action': { pt: 0, alignItems: 'center' },
              }}
              action={statusBanner.actionLabel ? (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Button
                    color="inherit"
                    size="small"
                    variant="outlined"
                    onClick={statusBanner.onAction}
                    disabled={statusBanner.disabled}
                    sx={{ fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap' }}
                  >
                    {statusBanner.actionLabel}
                  </Button>
                  {statusBanner.secondaryLabel && (
                    <Button
                      color="inherit"
                      size="small"
                      variant="text"
                      onClick={statusBanner.onSecondary}
                      sx={{ fontWeight: 700, borderRadius: 2, whiteSpace: 'nowrap' }}
                    >
                      {statusBanner.secondaryLabel}
                    </Button>
                  )}
                </Stack>
              ) : undefined}
            >
              {statusBanner.message}
            </Alert>
          )}
          {!managing ? (
          <>
          {/* Priority: plan (with renewal) + payment method, side by side. */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1.15fr 0.85fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {/* Plan */}
            <Card sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column' }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}
              >
                <Typography sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
                  {billPayment?.planDetails || 'Plan'}
                </Typography>
                {showCancelButton && !paymentRetry && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    disabled={cancelDisabled}
                    onClick={handleCancellation}
                    sx={{ fontWeight: 600, borderRadius: 2 }}
                  >
                    {cancelLabel}
                  </Button>
                )}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {subscriptionDetails?.subscription?.is_trial === '1'
                    ? `${subscriptionDetails?.current_plan} (Trial plan)`
                    : !subscriptionDetails?.expired
                      ? userDetails?.isSubscription?.plan || 'Free Plan'
                      : 'Plan Expired'}
                </Typography>
                <Tooltip
                  title={subscriptionDetails?.subscription?.is_trial === '1'
                    ? `Trial — ${getTrialAllowanceText()}`
                    : userDetails?.isSubscription && !subscriptionDetails?.expired
                      ? 'Unlimited uploads and Unlimited storage'
                      : 'Upgrade to a plan to upload and store your videos'}
                >
                  <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                </Tooltip>
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                {subscriptionDetails?.switch_plan_request !== null
                  ? `Plan upgrades to ${subscriptionDetails?.switch_plan_request?.new_plan_name} on ${formattedDate}`
                  : subscriptionDetails?.subscription?.is_trial === '1'
                    ? differenceInDays > 0
                      ? `Trial ends in ${differenceInDays} ${differenceInDays > 1 ? 'days' : 'day'}`
                      : 'Trial ends today'
                    : subscriptionDetails?.subscription?.is_cancelled === '1'
                      ? `Plan has been canceled and will expire on ${formattedDate}`
                      : formattedDate && !isMoreThan365Days && cardDetails?.lastAttempt?.payment_status !== '0'
                        ? !subscriptionDetails?.expired
                          ? `Plan renews on ${formattedDate}`
                          : `Plan expired on ${formattedDate}`
                        : null}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 'auto', pt: 2, flexWrap: 'wrap' }}>
                {subscriptionDetails?.subscription?.is_trial === '1' && (
                  <Button variant="contained" onClick={subscribePlan} disabled={loader}>
                    {loader ? 'Upgrading…' : `Upgrade to ${subscriptionDetails?.current_plan}`}
                  </Button>
                )}
                {subscriptionDetails?.subscription?.is_trial !== '1'
                  && subscriptionDetails?.subscription?.is_cancelled !== '1'
                  && !subscriptionDetails?.expired
                  && !userDetails?.isSubscription && (
                  <Button
                    component={Link}
                    to={userAccessedData ? '/affiliate' : Pathname.plans}
                    variant="contained"
                  >
                    {`Upgrade to ${/^(free|subscribe|upgrade)$/i.test(subscriptionDetails?.current_plan || '') ? 'paid' : subscriptionDetails?.current_plan} plan`}
                  </Button>
                )}
                {isPaidPlan
                  && subscriptionDetails?.subscription?.is_cancelled !== '1'
                  && subscriptionDetails?.switch_plan_request === null
                  && subscriptionDetails?.current_plan !== 'Lifetime'
                  && subscription_id !== '13'
                  && !subscriptionDetails?.expired && (
                  <Button variant="outlined" onClick={() => navigate('/switchplan')}>
                    Switch plan
                  </Button>
                )}
              </Stack>
            </Card>

            {/* Payment method */}
            <Card sx={{ p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column' }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}
              >
                <Typography sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
                  Payment method
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { setManagingAutoAdd(false); setManaging(true); }}
                  sx={{ borderRadius: 2 }}
                >
                  {billPayment?.manageInfo || 'Manage'}
                </Button>
              </Stack>
              <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flex: 1 }}>
                <Box
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    bgcolor: 'action.hover',
                    color: 'text.secondary',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <CreditCardRoundedIcon />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  {cardDetails.last4 ? (
                    <>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{cardDetails.brand}</Typography>
                        <Typography sx={{ fontWeight: 600 }}>{`•••• ${cardDetails.last4}`}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                        {cardDetails.month && cardDetails.year && (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {`Expires ${String(cardDetails.month).padStart(2, '0')}/${cardDetails.year}`}
                          </Typography>
                        )}
                        {isExpired && <Chip size="small" color="error" variant="outlined" label="Card expired" sx={{ height: 20 }} />}
                      </Stack>
                    </>
                  ) : (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>No card on file</Typography>
                  )}
                </Box>
              </Stack>
            </Card>
          </Box>

          {/* Billing information — static; edits are made on the Account tab. */}
          <Card sx={{ p: { xs: 2, md: 2.5 } }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 11 }}>
                Billing information
              </Typography>
              <Stack direction="row" spacing={1}>
                {/* <Button
                  size="small"
                  variant="outlined"
                  startIcon={<HistoryRoundedIcon fontSize="small" />}
                  onClick={() => setShowHistory(true)}
                  sx={{ borderRadius: 2 }}
                >
                  View history
                </Button> */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => navigate(Pathname.getSettingsPath(Pathname.settings.accounts))}
                  sx={{ borderRadius: 2 }}
                >
                  Edit in Account
                </Button>
              </Stack>
            </Stack>
            <Box>
              <FieldRow icon={EmailRoundedIcon} label="Account email" value={userDetails?.email} />
              <Divider />
              <FieldRow icon={PhoneRoundedIcon} label="Phone number" value={userDetails?.mobile} />
            </Box>
          </Card>
          </>
          ) : (
            <PaymentMethodsManager onClose={() => setManaging(false)} autoAdd={managingAutoAdd} />
          )}
        </Stack>
      </V2SettingsShell>
      {/* Popovers render outside the shell's theme provider, so wrap them to
          inherit the V2 (Montserrat) theme. */}
      <V2ThemeProvider withBaseline={false}>
      <Dialog
        open={openCencellationModal}
        onClose={() => setOpenCencellationModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
        aria-labelledby="cancel-subscription-dialog"
      >
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            {subscriptionDetails?.subscription?.is_trial === '0'
              ? cancellationText?.title || 'Are you done sharing?'
              : 'Stop sharing your memories?'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            {cancellationText?.description || 'If you are sure you’d like to cancel your subscription, finish below.'}
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, mb: 3, color: 'text.secondary' }}>
            {subscriptionDetails?.subscription?.is_trial === '0' && (
              <li>
                {cancellationText?.pointOne || 'Cancellation will be effective at the end of your current billing period on'}
                {' '}
                {`${trial_end.day}/${trial_end.month}/${trial_end.year}`}
              </li>
            )}
            {subscriptionDetails?.subscription?.is_trial !== '0' && (
              <li>By canceling your trial you will no longer have access to your content.</li>
            )}
            <li>{cancellationText?.pointTwo || 'Come back anytime. If you restart within 30 days your profiles, videos, viewers and account details will be waiting for you.'}</li>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button
              variant="text"
              onClick={() => setOpenCencellationModal(false)}
              disabled={cancellingSubscription || !done || isCancelClicked}
            >
              {commonText?.goBack || 'Go back'}
            </Button>
            <Button
              variant="contained"
              color="error"
              disabled={cancellingSubscription || !done || isCancelClicked}
              onClick={handleCancelSubscription}
            >
              {isCancelClicked
                ? 'Canceling…'
                : cancellingSubscription
                  ? !done ? 'Canceled' : 'Canceling…'
                  : subscriptionDetails?.subscription?.is_trial === '1'
                    ? 'Finish trial cancellation'
                    : (cancellationText?.finish || 'Finish cancellation')}
            </Button>
          </Stack>
        </Box>
      </Dialog>
      <Dialog
        open={renewModalOpen}
        onClose={() => setRenewModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <Box sx={{ p: { xs: 3, md: 4 } }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
            Renew your subscription
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            We&apos;d love to have you back! Renewing your subscription will restore full access to all features and your saved content.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
            Your subscription will be renewed with your current payment method on file.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ justifyContent: 'flex-end' }}>
            <Button variant="text" onClick={() => setRenewModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={reactivateSubscription} disabled={reactivating}>
              {reactivating ? 'Reactivating…' : 'Renew subscription'}
            </Button>
          </Stack>
        </Box>
      </Dialog>

      {/* Transaction history — mock data (no backend endpoint yet). */}
      <Dialog
        open={showHistory}
        onClose={() => setShowHistory(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <Box sx={{ p: { xs: 2.5, md: 3 } }}>
          <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Box>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>Transaction history</Typography>
                <Chip size="small" label="Coming soon" sx={{ height: 20, fontWeight: 700, bgcolor: 'action.hover' }} />
              </Stack>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>
                Every payment made from your projector. Sample data shown — live history is on the way.
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setShowHistory(false)} aria-label="Close">
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Stack>
          <TableContainer sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 700, color: 'text.secondary', whiteSpace: 'nowrap' } }}>
                  <TableCell>Amount</TableCell>
                  <TableCell>Card Last 4</TableCell>
                  <TableCell>Date of Capture</TableCell>
                  <TableCell>Billing Name</TableCell>
                  <TableCell>Projector Name</TableCell>
                  <TableCell align="right">Dispute</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {MOCK_TRANSACTIONS.map((t) => (
                  <TableRow key={t.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{t.amount}</TableCell>
                    <TableCell>{`•••• ${t.last4}`}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.date}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.billingName}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{t.projector}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Disputes open once live billing history ships">
                        <span>
                          <Button size="small" variant="outlined" color="error" disabled>
                            Dispute
                          </Button>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Dialog>
      </V2ThemeProvider>
    </>
  );
};
